// Port of api/fuel/app/classes/filter/core/search/rule.php
// Base class for advanced mode rules. Extends Unit with logic operators.

import DB from 'offline/db'
import Util from 'offline/util'
import Constants from 'offline/constants'

var Rule = {

	// Create a rule definition object. Each query/post rule calls this.
	create: function(def) {
		var rule = {
			name: def.name,
			_priority: def.priority || 50,
			_config: false,

			priority: function() { return rule._priority },

			field: def.field || function() { return 'g.game_id' },
			fieldLow: def.fieldLow || function() { return rule.field() },
			fieldHigh: def.fieldHigh || function() { return rule.field() },

			isIndex: def.isIndex || function() { return false },
			isRange: def.isRange || function() { return false },

			// Lifecycle hooks
			pre: def.pre || function() {},
			post: def.post || function() {},

			// Custom logic overrides
			_match: def.match || null,
			_min: def.min || null,
			_max: def.max || null,
			_range: def.range || null,
			_tolerance: def.tolerance || null,
			_rolling: def.rolling || null,
			_oneof: def.oneof || null,
			_someof: def.someof || null,
			_allof: def.allof || null,
			_contains: def.contains || null,

			// Custom value/operand overrides
			_value: def.value || null,

			require: def.require || function(config, filter) {
				if (!filter.rules || !filter.rules.length) return false
				var name = def.name
				for (var i = 0; i < filter.rules.length; i++) {
					var ruleType = filter.rules[i].type
					if (ruleType && ruleType.replace(/\./g, '_') === name) return true
				}
				return false
			},

			run: function(config, filter, data, query) {
				var name = def.name
				rule._config = config

				for (var i = 0; i < filter.rules.length; i++) {
					var r = filter.rules[i]
					var ruleType = r.type ? r.type.replace(/\./g, '_') : ''
					if (ruleType !== name) continue

					var logic = (r.setting && r.setting.logic) || 'match'

					// Call pre hook
					rule.pre(r, config, filter, data, query)

					// Dispatch to logic method
					if (rule['_' + logic]) {
						rule['_' + logic](r, config, filter, data, query)
					} else if (Rule.defaults[logic]) {
						Rule.defaults[logic].call(rule, r, config, filter, data, query)
					}

					// Call post hook
					rule.post(r, config, filter, data, query)
				}
			}
		}

		return rule
	},

	// Default implementations of logic methods
	defaults: {

		match: function(rule, config, filter, data, query) {
			if (this.isIndex()) {
				var opMap = Rule.operandMapped(rule, { '=': 'in', '!=': 'not in' })
				query.where(this.field(rule), opMap, Rule.gameIndex(this, rule, 'one', data, config))
			} else if (this.isRange()) {
				var rangeSetting = (rule.setting && rule.setting.range) || 'low'

				if (rangeSetting === 'low') {
					query.where(this.fieldLow(), Rule.operand(rule, ['=', '!=']), Rule.value(rule))
				} else if (rangeSetting === 'high') {
					query.where(this.fieldHigh(), Rule.operand(rule, ['=', '!=']), Rule.value(rule))
				} else if (rangeSetting === 'exact') {
					query.where(this.fieldLow(), Rule.operand(rule, ['=', '!=']), Rule.value(rule))
					query.where(this.fieldHigh(), Rule.operand(rule, ['=', '!=']), Rule.value(rule))
				} else if (rangeSetting === 'within') {
					if (rule.logic.operand === '=') {
						query.where(this.fieldLow(), '<=', Rule.value(rule))
						query.where(this.fieldHigh(), '>=', Rule.value(rule))
					} else {
						query.whereGroup('OR', [
							[this.fieldLow(), '>', Rule.value(rule)],
							[this.fieldHigh(), '<', Rule.value(rule)]
						])
					}
				}
			} else if (Rule.isSettingPeriod(rule)) {
				if (rule.logic.operand === '=') {
					query.where(this.field(), '>=', Rule.period_from(rule.logic.value, rule.setting.period))
					query.where(this.field(), '<=', Rule.period_to(rule.logic.value, rule.setting.period))
				} else {
					query.whereGroup('OR', [
						[this.field(), '<', Rule.period_from(rule.logic.value, rule.setting.period)],
						[this.field(), '>', Rule.period_to(rule.logic.value, rule.setting.period)]
					])
				}
			} else {
				query.where(this.field(), Rule.operand(rule, ['=', '!=']), Rule.value(rule))
			}
		},

		min: function(rule, config, filter, data, query) {
			if (this.isRange()) {
				var rangeSetting = (rule.setting && rule.setting.range) || 'low'

				if (rangeSetting === 'low') {
					query.where(this.fieldLow(), Rule.operand(rule, ['>=', '>']), Rule.value(rule))
				} else if (rangeSetting === 'high') {
					query.where(this.fieldHigh(), Rule.operand(rule, ['>=', '>']), Rule.value(rule))
				} else if (rangeSetting === 'within') {
					if (rule.logic.operand === '>=') {
						query.where(this.fieldLow(), '<=', Rule.value(rule))
						query.where(this.fieldHigh(), '>=', Rule.value(rule))
					} else if (rule.logic.operand === '>') {
						query.where(this.fieldLow(), '<', Rule.value(rule))
						query.where(this.fieldHigh(), '>=', Rule.value(rule))
					}
				}
			} else if (Rule.isSettingPeriod(rule)) {
				query.where(this.field(), Rule.operand(rule, ['>=', '>']), Rule.period_from(rule.logic.value, rule.setting.period))
			} else {
				query.where(this.field(), Rule.operand(rule, ['>=', '>']), Rule.value(rule))
			}
		},

		max: function(rule, config, filter, data, query) {
			if (this.isRange()) {
				var rangeSetting = (rule.setting && rule.setting.range) || 'low'

				if (rangeSetting === 'low') {
					query.where(this.fieldLow(), Rule.operand(rule, ['<=', '<']), Rule.value(rule))
				} else if (rangeSetting === 'high') {
					query.where(this.fieldHigh(), Rule.operand(rule, ['<=', '<']), Rule.value(rule))
				} else if (rangeSetting === 'within') {
					if (rule.logic.operand === '<=') {
						query.where(this.fieldLow(), '<=', Rule.value(rule))
						query.where(this.fieldHigh(), '>=', Rule.value(rule))
					} else if (rule.logic.operand === '<') {
						query.where(this.fieldLow(), '<=', Rule.value(rule))
						query.where(this.fieldHigh(), '>', Rule.value(rule))
					}
				}
			} else if (Rule.isSettingPeriod(rule)) {
				query.where(this.field(), Rule.operand(rule, ['<=', '<']), Rule.period_to(rule.logic.value, rule.setting.period))
			} else {
				query.where(this.field(), Rule.operand(rule, ['<=', '<']), Rule.value(rule))
			}
		},

		range: function(rule, config, filter, data, query) {
			if (this.isRange()) {
				var rangeSetting = (rule.setting && rule.setting.range) || 'low'

				if (rangeSetting === 'low') {
					query.where(this.fieldLow(), '>=', Rule.value(rule))
					query.where(this.fieldLow(), '<=', Rule.value(rule, 'to'))
				} else if (rangeSetting === 'high') {
					query.where(this.fieldHigh(), '>=', Rule.value(rule))
					query.where(this.fieldHigh(), '<=', Rule.value(rule, 'to'))
				} else if (rangeSetting === 'exact') {
					query.where(this.fieldLow(), '=', Rule.value(rule))
					query.where(this.fieldHigh(), '=', Rule.value(rule, 'to'))
				} else if (rangeSetting === 'within') {
					query.where(this.fieldLow(), '<=', Rule.value(rule))
					query.where(this.fieldHigh(), '>=', Rule.value(rule, 'to'))
				}
			} else if (Rule.isSettingPeriod(rule)) {
				query.where(this.field(), '>=', Rule.period_from(rule.logic.value, rule.setting.period))
				query.where(this.field(), '<=', Rule.period_to(rule.logic.to, rule.setting.period))
			} else {
				query.where(this.field(), '>=', rule.logic.value)
				query.where(this.field(), '<=', rule.logic.to)
			}
		},

		tolerance: function(rule, config, filter, data, query) {
			var tol = Rule.value(rule, 'tolerance') || 0

			if (this.isRange()) {
				var rangeSetting = (rule.setting && rule.setting.range) || 'low'

				if (rangeSetting === 'low') {
					query.where(this.fieldLow(), '>=', Rule.value(rule) - tol)
					query.where(this.fieldLow(), '<=', parseFloat(Rule.value(rule)) + parseFloat(tol))
				} else if (rangeSetting === 'high') {
					query.where(this.fieldHigh(), '>=', Rule.value(rule) - tol)
					query.where(this.fieldHigh(), '<=', parseFloat(Rule.value(rule)) + parseFloat(tol))
				} else if (rangeSetting === 'exact') {
					query.where(this.fieldLow(), '>=', Rule.value(rule) - tol)
					query.where(this.fieldLow(), '<=', parseFloat(Rule.value(rule)) + parseFloat(tol))
					query.where(this.fieldHigh(), '>=', Rule.value(rule) - tol)
					query.where(this.fieldHigh(), '<=', parseFloat(Rule.value(rule)) + parseFloat(tol))
				} else if (rangeSetting === 'within') {
					query.where(this.fieldLow(), '<=', Rule.value(rule) - tol)
					query.where(this.fieldHigh(), '>=', parseFloat(Rule.value(rule)) + parseFloat(tol))
				}
			} else {
				query.where(this.field(), '>=', Rule.value(rule) - tol)
				query.where(this.field(), '<=', parseFloat(Rule.value(rule)) + parseFloat(tol))
			}
		},

		rolling: function(rule, config, filter, data, query) {
			var tolerance = rule.logic.value
			var period = rule.setting.period
			var now = Math.floor(Date.now() / 1000)
			var date = now

			if (period === 'day') {
				date = now - (Constants.TIME_DAY * tolerance)
			} else if (period === 'week') {
				date = now - (Constants.TIME_WEEK * tolerance)
			} else if (period === 'month') {
				date = now - (Constants.TIME_MONTH * tolerance)
			} else if (period === 'year') {
				date = now - (Constants.TIME_YEAR * tolerance)
			}

			query.where(this.field(), '>=', date)
		},

		oneof: function(rule, config, filter, data, query) {
			var opMap = Rule.operandMapped(rule, { '=': 'in', '!=': 'not in' })
			query.where(this.field(rule), opMap, Rule.gameIndex(this, rule, 'one', data, config))
		},

		someof: function(rule, config, filter, data, query) {
			query.where(this.field(rule), 'in', Rule.gameIndex(this, rule, 'some', data, config))
		},

		allof: function(rule, config, filter, data, query) {
			query.where(this.field(rule), 'in', Rule.gameIndex(this, rule, 'all', data, config))
		}
	},

	// Utility methods

	value: function(rule, name) {
		name = name || 'value'
		if (rule.logic && rule.logic[name] !== undefined) return rule.logic[name]
		return null
	},

	valueAsIndex: function(rule) {
		var values = rule.logic && rule.logic.value
		if (!values) return []
		if (!Array.isArray(values)) return []

		var index = []
		for (var i = 0; i < values.length; i++) {
			var parts = String(values[i]).split('-')
			index.push(parseInt(parts[0], 10))
		}

		return index
	},

	operand: function(rule, allowed) {
		var operand = (rule.logic && rule.logic.operand) || '='
		if (allowed && allowed.indexOf(operand) === -1) operand = allowed[0]
		if (operand === '!=') operand = '<>'
		return operand
	},

	operandMapped: function(rule, map) {
		var operand = (rule.logic && rule.logic.operand) || '='
		var allowed = Object.keys(map)
		if (allowed.indexOf(operand) === -1) operand = allowed[0]
		return map[operand]
	},

	isSettingPeriod: function(rule) {
		return rule.setting && rule.setting.period && ['day', 'week', 'month', 'year'].indexOf(rule.setting.period) !== -1
	},

	setFilter: function(rule, logic, index) {
		if (logic !== 'one') {
			var counts = {}
			for (var i = 0; i < index.length; i++) {
				var id = index[i]
				counts[id] = (counts[id] || 0) + 1
			}

			var tolerance
			if (logic === 'some') {
				tolerance = rule.logic.tolerance
			} else {
				// 'all'
				tolerance = Rule.valueAsIndex(rule).length
			}

			var operand = (rule.logic && rule.logic.operand) || '>='
			var keys = Object.keys(counts)
			index = []

			for (var k = 0; k < keys.length; k++) {
				var v = counts[keys[k]]
				var pass = false

				if (operand === '>=') pass = v >= tolerance
				else if (operand === '>') pass = v > tolerance
				else if (operand === '<=') pass = v <= tolerance
				else if (operand === '<') pass = v < tolerance
				else if (operand === '=') pass = v == tolerance
				else if (operand === '!=') pass = v != tolerance

				if (pass) index.push(parseInt(keys[k], 10))
			}
		}

		// Unique
		var seen = {}
		var unique = []
		for (var u = 0; u < index.length; u++) {
			if (!seen[index[u]]) {
				seen[index[u]] = true
				unique.push(index[u])
			}
		}

		return unique.length ? unique : [0]
	},

	gameIndex: function(self, rule, logic) {
		var index = Rule.valueAsIndex(rule)
		if (!index.length) return [0]

		var indexType = self.isIndex()

		if (indexType === 'game') {
			return Rule.setFilter(rule, logic, index)
		}

		var table = 'game_' + indexType
		var field = indexType + '_id'

		var rows = DB.query(
			'SELECT game_id FROM ' + table + ' WHERE ' + field + ' IN(' + Util.in(index) + ')'
		)
		var gameIndex = Util.pluck(rows, 'game_id')

		return Rule.setFilter(rule, logic, gameIndex)
	},

	period_from: function(value, type) {
		if (!value) return 0

		if (type === 'year') {
			var d = new Date(Date.UTC(parseInt(value, 10), 0, 1, 0, 0, 0))
			return Math.floor(d.getTime() / 1000)
		} else if (type === 'month') {
			var parts
			if (typeof value === 'string' && value.indexOf('/') !== -1) {
				parts = value.split('/')
				var md = new Date(Date.UTC(parseInt(parts[1], 10), parseInt(parts[0], 10) - 1, 1, 0, 0, 0))
				return Math.floor(md.getTime() / 1000)
			}
			var mt = new Date((typeof value === 'number' ? value : parseInt(value, 10)) * 1000)
			var ms = new Date(Date.UTC(mt.getUTCFullYear(), mt.getUTCMonth(), 1, 0, 0, 0))
			return Math.floor(ms.getTime() / 1000)
		} else if (type === 'week') {
			var ts
			if (typeof value === 'string' && value.indexOf('/') !== -1) {
				parts = value.split('/')
				ts = new Date(Date.UTC(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10), 0, 0, 0))
			} else {
				ts = new Date((typeof value === 'number' ? value : parseInt(value, 10)) * 1000)
			}
			// Get Monday of the week
			var day = ts.getUTCDay()
			var diff = day === 0 ? -6 : 1 - day
			var monday = new Date(Date.UTC(ts.getUTCFullYear(), ts.getUTCMonth(), ts.getUTCDate() + diff, 0, 0, 0))
			return Math.floor(monday.getTime() / 1000)
		} else if (type === 'day') {
			if (typeof value === 'string' && value.indexOf('/') !== -1) {
				parts = value.split('/')
				var dd = new Date(Date.UTC(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10), 0, 0, 0))
				return Math.floor(dd.getTime() / 1000)
			}
			var dt = new Date((typeof value === 'number' ? value : parseInt(value, 10)) * 1000)
			var ds = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate(), 0, 0, 0))
			return Math.floor(ds.getTime() / 1000)
		}

		return 0
	},

	period_to: function(value, type) {
		if (!value) return 0

		if (type === 'year') {
			var d = new Date(Date.UTC(parseInt(value, 10), 11, 31, 23, 59, 59))
			return Math.floor(d.getTime() / 1000)
		} else if (type === 'month') {
			var parts
			if (typeof value === 'string' && value.indexOf('/') !== -1) {
				parts = value.split('/')
				// First day of next month minus 1 second
				var md = new Date(Date.UTC(parseInt(parts[1], 10), parseInt(parts[0], 10), 1, 0, 0, 0))
				return Math.floor(md.getTime() / 1000) - 1
			}
			var mt = new Date((typeof value === 'number' ? value : parseInt(value, 10)) * 1000)
			var me = new Date(Date.UTC(mt.getUTCFullYear(), mt.getUTCMonth() + 1, 1, 0, 0, 0))
			return Math.floor(me.getTime() / 1000) - 1
		} else if (type === 'week') {
			var from = Rule.period_from(value, 'week')
			return from + Constants.TIME_WEEK - 1
		} else if (type === 'day') {
			var dayFrom = Rule.period_from(value, 'day')
			return dayFrom + Constants.TIME_DAY - 1
		}

		return 0
	},

	// Sort helpers (for rules that also need sort awareness)
	sort: function(name, filter) {
		if (!Array.isArray(name)) name = [name]
		var sorts = filter.sort.replace(/-/g, '').split(',')
		var count = 0
		for (var i = 0; i < name.length; i++) {
			if (sorts.indexOf(name[i]) !== -1) count++
		}
		return count
	},

	sortReverse: function(name, filter) {
		if (!Array.isArray(name)) name = [name]
		var alteredName = []
		for (var i = 0; i < name.length; i++) {
			alteredName.push('-' + name[i])
		}
		var sorts = filter.sort.split(',')
		var count = 0
		for (var j = 0; j < alteredName.length; j++) {
			if (sorts.indexOf(alteredName[j]) !== -1) count++
		}
		return count || (name.indexOf(filter.sort) !== -1 && filter.sortReverse)
	},

	sortPriority: function(name, filter, def) {
		def = def || 10000
		if (!Array.isArray(name)) name = [name]
		var index = false
		var sorts = filter.sort.replace(/-/g, '').split(',')
		for (var i = 0; i < name.length; i++) {
			var candidateIndex = sorts.indexOf(name[i])
			if (candidateIndex !== -1 && (index === false || candidateIndex < index)) {
				index = candidateIndex
			}
		}
		return (index === false) ? def : 1 + index
	},

	// Get keywords from a text value (splits on spaces, quotes for phrases)
	getKeywords: function(text) {
		if (!text) return []
		var keywords = []
		var matches = text.match(/"[^"]+"|[^\s]+/g)
		if (matches) {
			for (var i = 0; i < matches.length; i++) {
				keywords.push(matches[i].replace(/"/g, ''))
			}
		}
		return keywords
	}

}

export default Rule
